import { useState, type ReactNode } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import ClaimRow from '@/karehero-app/tell-us-once/intake/ClaimRow';
import ClaimSheet from '@/karehero-app/tell-us-once/benefits/ClaimSheet';
import { CALCULATOR_RESULT } from '@/karehero-app/tell-us-once/benefits/BenefitsCalculator.content';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';

// PROPOSED. What the calculator found, in the rows the intake already uses for a
// claim: one coral row for the one to do next, and the rest in lime under it.
//
// The amount is on the row rather than in a headline. A single big number is a
// promise, and this is an estimate that a claim decides — so the workings sit
// under the list and say what it was worked out from.
export type Claim = (typeof CALCULATOR_RESULT)['claims'][number] & {
  /** What it is, in the drawer a tap on its row opens. */
  about?: string;
};

/** What the calculator found, for the results page or the profile. */
export type ResultContent = {
  title: string;
  /** A line under the title. */
  body?: string;
  claims: Claim[];
  primary?: string;
  secondary?: string;
  /** The button in a claim's drawer. */
  start?: string;
};

const ResultScreen = ({
  onStart,
  onTalk,
  bar,
  content = CALCULATOR_RESULT,
  openClaim,
}: {
  onStart: () => void;
  onTalk: () => void;
  /** The calculator's app bar. Omit and the screen has none. */
  bar?: ReactNode;
  /**
   * What it found. The 5 October proposal's by default. Without a primary,
   * there are no buttons at the foot: each claim is started from its drawer.
   */
  content?: ResultContent;
  /** Harness-only: open with this claim's drawer up. */
  openClaim?: string;
}) => {
  const [open, setOpen] = useState<Claim | undefined>(
    content.claims.find((c) => c.name === openClaim),
  );

  return (
    <ScreenRoot relative>
      <StatusBar />
      {bar}

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 3, pb: 2 }}>
        <Stack sx={{ pt: PAGE_HEADER_TOP }} spacing={3}>
          <Stack spacing={1}>
            <PageHeader title={content.title} variant='h2' />
            {content.body && (
              <Typography variant='body1'>{content.body}</Typography>
            )}
          </Stack>

          <Stack spacing={2}>
            {content.claims.map((claim) => (
              <ClaimRow
                key={claim.name}
                icon={claim.icon}
                name={claim.name}
                description={claim.description}
                lead={claim.lead}
                onClick={claim.about ? () => setOpen(claim) : undefined}
              />
            ))}
          </Stack>
        </Stack>
      </Box>

      {content.primary && (
        <Box
          sx={{
            px: 3,
            pt: 1.5,
            pb: 0.5,
            bgcolor: 'background.default',
            borderTop: 1,
            borderColor: 'custom.neutral.200',
          }}
        >
          <Stack spacing={2}>
            <Button
              variant='primary'
              size='lg'
              isFullWidth
              ariaLabel={content.primary}
              onPress={onStart}
            >
              {content.primary}
            </Button>
            {content.secondary && (
              <Button
                variant='secondary'
                size='lg'
                isFullWidth
                ariaLabel={content.secondary}
                onPress={onTalk}
              >
                {content.secondary}
              </Button>
            )}
          </Stack>
        </Box>
      )}

      <HomeIndicator />

      {/* What a claim is, and the way into it. */}
      {open?.about && (
        <ClaimSheet
          name={open.name}
          about={open.about}
          start={content.start}
          onStart={onStart}
          onClose={() => setOpen(undefined)}
        />
      )}
    </ScreenRoot>
  );
};

export default ResultScreen;
